import { createSelector } from 'reselect';
import { products } from "../../../reducers/products";
import { getSubState } from '../../../reducers/subReducerHelpers';
import { UPSELL_TYPE_PRODUCT, genSubReducerKey } from '../../../views/upsells/reducers/upsells';
import { calculateConfigurationQtyArray } from '../../../views/product-picker/reducers/product-picker';

const getProductSales       = state => state.productSales.products;
const getProducts           = state => state.products.products;
const getSelectedValues     = state => Object.values(state.productPicker.productsQuantities);
const getSelectedProducts   = state => state.productPicker.productsQuantities;
const getConfigQuantities   = state => state.productPicker.configQuantities;

const formatProductSaleData = (productSale) => {
    return {
        configuration_id: productSale.configuration_id,
        configurationName: productSale.display_name,
        productName: productSale.product_name,
        price: productSale.cost.amount,
        total_price: productSale.cost.amount,
        fee: productSale.buyer_fee.amount,
        total_fee: productSale.buyer_fee.amount,
        quantity: 1,
        subtotal: productSale.cost.amount + productSale.buyer_fee.amount,
        currency: productSale.cost.currency,
        currency_symbol: productSale.cost.symbol,
        event_id: productSale.event_id,
        quantities: calculateConfigurationQtyArray(productSale?.configuration_transaction_limit ?? 10),
        description: productSale.description,
        dependentProduct: productSale.dependentProduct
    };
}

/**
 * Group non upsell product sales by event ID and config ID
 */
export const getProductSalesByEvent = createSelector(
    [getProductSales],
    (productSales) => {
        const selectedProducts = {};
        for(let i = 0; i < productSales.length; i++) {
            if (productSales[i].listing_upsell_id) {
                continue;
            }
            if (!selectedProducts[productSales[i].event_id]) {
                selectedProducts[productSales[i].event_id] = [formatProductSaleData(productSales[i])];
            }  else {
                let found = false;
                for(let j = 0; j < selectedProducts[productSales[i].event_id].length; j++) {
                    if(selectedProducts[productSales[i].event_id][j].configuration_id === productSales[i].configuration_id) {
                        selectedProducts[productSales[i].event_id][j].total_price += productSales[i].cost.amount;
                        selectedProducts[productSales[i].event_id][j].total_fee += productSales[i].buyer_fee.amount;
                        selectedProducts[productSales[i].event_id][j].subtotal += (productSales[i].cost.amount + productSales[i].buyer_fee.amount);
                        selectedProducts[productSales[i].event_id][j].quantity++;
                        found = true;
                        break;
                    }
                }

                if(!found) {
                    selectedProducts[productSales[i].event_id].push(formatProductSaleData(productSales[i]));
                }
            }
        }

        return selectedProducts;
    }
);

/**
 * Group upsell product sales by listing upsell ID and config ID
 */
export const getUpsoldProductsByListingUpsellId = createSelector(
    [getProductSales],
    (productSales) => {
        const selectedProducts = {};
        for(let i = 0; i < productSales.length; i++) {
            if (!productSales[i].listing_upsell_id) {
                continue;
            }
            if (!selectedProducts[productSales[i].listing_upsell_id]) {
                selectedProducts[productSales[i].listing_upsell_id] = [formatProductSaleData(productSales[i])];
            }  else {
                let found = false;
                for(let j = 0; j < selectedProducts[productSales[i].listing_upsell_id].length; j++) {
                    if(selectedProducts[productSales[i].listing_upsell_id][j].configuration_id === productSales[i].configuration_id) {
                        selectedProducts[productSales[i].listing_upsell_id][j].total_price += productSales[i].cost.amount;
                        selectedProducts[productSales[i].listing_upsell_id][j].total_fee += productSales[i].buyer_fee.amount;
                        selectedProducts[productSales[i].listing_upsell_id][j].subtotal += (productSales[i].cost.amount + productSales[i].buyer_fee.amount);
                        selectedProducts[productSales[i].listing_upsell_id][j].quantity++;
                        found = true;
                        break;
                    }
                }
                if(!found) {
                    selectedProducts[productSales[i].listing_upsell_id].push(formatProductSaleData(productSales[i]));
                }
            }
        }
        return selectedProducts;
    }
);

export const getConfigurationQuantitiesFromProductSales = (
  productSales,
  listingUpsellId = null,
) => {
  const productsByEvent = {};
  for (let i = 0; i < productSales.length; i++) {
    const sale = productSales[i];
    if ((!listingUpsellId && !sale?.listing_upsell_id) || (+sale?.listing_upsell_id === +listingUpsellId)) {
      const eventId = sale.event_id;
      const configId = sale.configuration_id;
      if (!productsByEvent[eventId]) {
        productsByEvent[eventId] = {};
      }
      if (!productsByEvent[eventId][configId]) {
        productsByEvent[eventId][configId] = 1;
      } else {
        productsByEvent[eventId][configId]++;
      }
    }
  }
  return productsByEvent;
};


export const getTotalSelectedProducts = createSelector(
    [getSelectedValues],
    (products) => {
        return getTotalSelectedProductsFromState(products);
    }
);

export const getTotalSelectedProductsFromState = providedProducts => {
    return providedProducts.reduce((total, val) => {
        return total + (val === undefined ? 0 : val);
    }, 0);
}

export const cartHasDependentProducts = createSelector(
    [getProductSales],
    (productSales) => {
        let countDependentProducts = 0;
        for(let i = 0; i < productSales.length; i++) {
            if (productSales[i].dependentProduct) {
                countDependentProducts++;
            }
        }

        return (countDependentProducts > 0);
    }
);

export const isDependentProductSelected = createSelector(
    [getProducts, getSelectedProducts],
    (products, selectedProducts) => {
        const dependentIds = [];
        Object.values(products).forEach((product, i) => {
            if (!product.purchasable_without_tickets) {
                dependentIds.push(product.product_id);
            }
        });

        let countDependentProducts = 0;
        Object.keys(selectedProducts).forEach(selectedId => {
            if (selectedProducts[selectedId] !== undefined && dependentIds.indexOf(selectedId) !== -1) {
                countDependentProducts++;
            }
        });

        return countDependentProducts > 0;
    }
);

export const getProductsWithErrors = createSelector(
    [getSelectedProducts, getConfigQuantities, getProducts],
    (selectedProducts, configurationQuantities, products) => {
        const errors = {};
        for (const productId in selectedProducts) {
            if (selectedProducts[productId] !== undefined &&
                (configurationQuantities[productId] === undefined ||
                    selectedProducts[productId] !== Object.keys(configurationQuantities[productId]).length)) {
                if (!errors[productId]) {
                    errors[productId] = [{
                        productId: products[productId].product_id,
                        productName: products[productId].name,
                        optionName: products[productId].configurations[0].optionName,
                    }];
                }
            }
        }

        return errors;
    }
);
